import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Download, X, Receipt, Wand2 } from "lucide-react";

const STATUS_PILL = { draft: "warn", sent: "forest", paid: "success", overdue: "error", void: "" };

const monthOptions = () => {
  const arr = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("default", { month: "long", year: "numeric" });
    arr.push({ value, label });
  }
  return arr;
};

const downloadFile = async (url, filename) => {
  const token = localStorage.getItem("hbt_token");
  const res = await fetch(`${API}${url}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

export default function Invoices() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientMap, setClientMap] = useState({});
  const [showGen, setShowGen] = useState(false);
  const [gen, setGen] = useState({ client_id: "", month: monthOptions()[0].value, tax_rate: 15, due_days: 30 });
  const [busy, setBusy] = useState(false);
  const months = monthOptions();

  const load = () => api.get("/invoices").then((r) => setItems(r.data));

  useEffect(() => {
    load();
    api.get("/clients").then((r) => {
      setClients(r.data);
      const m = {};
      r.data.forEach((c) => (m[c.id] = c.name));
      setClientMap(m);
    });
  }, []);

  const submitGen = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/generate", { ...gen, tax_rate: Number(gen.tax_rate), due_days: Number(gen.due_days) });
      toast.success(`Invoice ${data.invoice_number} created`);
      setShowGen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate invoice");
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (inv, status) => {
    await api.patch(`/invoices/${inv.id}`, { status });
    toast.success(`Marked ${status}`);
    load();
  };

  const remove = async (inv) => {
    if (!window.confirm(`Delete ${inv.invoice_number}?`)) return;
    await api.delete(`/invoices/${inv.id}`);
    toast.success("Deleted"); load();
  };

  const dl = async (inv) => {
    try { await downloadFile(`/invoices/${inv.id}/pdf`, `${inv.invoice_number}.pdf`); }
    catch { toast.error("PDF download failed"); }
  };

  const totalOutstanding = items.filter((i) => i.status === "sent" || i.status === "overdue").reduce((s, i) => s + (i.total || 0), 0);
  const totalPaid = items.filter((i) => i.status === "paid").reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div>
      <PageHeader
        eyebrow="Operations"
        title="Invoices"
        subtitle="Generate invoices from collected waste streams. Download branded PDFs and track payment status."
        actions={
          <button
            onClick={() => { setGen({ ...gen, client_id: clients[0]?.id || "" }); setShowGen(true); }}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="generate-invoice-btn"
          >
            <Wand2 className="w-4 h-4" /> Generate from month
          </button>
        }
      />
      <div className="px-8 lg:px-12 py-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="hbt-card p-5"><div className="hbt-label">Total invoices</div><div className="font-display text-3xl font-semibold mt-2">{items.length}</div></div>
          <div className="hbt-card p-5"><div className="hbt-label">Outstanding (SZL)</div><div className="font-display text-3xl font-semibold mt-2 font-mono-num" style={{ color: "var(--terracotta)" }}>{totalOutstanding.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div></div>
          <div className="hbt-card p-5"><div className="hbt-label">Paid (SZL)</div><div className="font-display text-3xl font-semibold mt-2 font-mono-num" style={{ color: "var(--forest)" }}>{totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div></div>
        </div>

        <div className="hbt-card overflow-hidden">
          <table className="hbt-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Client</th>
                <th>Period</th>
                <th>Issued</th>
                <th>Due</th>
                <th className="num">Total</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <td className="font-mono text-xs">{inv.invoice_number}</td>
                  <td>{clientMap[inv.client_id] || "—"}</td>
                  <td className="text-neutral-600">{inv.period || "—"}</td>
                  <td className="font-mono-num">{inv.issue_date}</td>
                  <td className="font-mono-num">{inv.due_date}</td>
                  <td className="num font-mono-num font-semibold">{inv.currency} {inv.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td>
                    <select
                      value={inv.status}
                      onChange={(e) => setStatus(inv, e.target.value)}
                      className={`pill ${STATUS_PILL[inv.status] || ""} text-[11px] py-1 pr-6 pl-3 border-0 cursor-pointer`}
                      data-testid={`invoice-status-${inv.id}`}
                    >
                      {["draft","sent","paid","overdue","void"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td>
                    <div className="flex items-center gap-1">
                      <button onClick={() => dl(inv)} className="text-xs text-neutral-500 hover:text-black inline-flex items-center gap-1" data-testid={`invoice-pdf-${inv.id}`}>
                        <Download className="w-4 h-4" />
                      </button>
                      <button onClick={() => remove(inv)} className="text-xs text-neutral-500 hover:text-red-700 ml-2" data-testid={`invoice-delete-${inv.id}`}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={8} className="text-center text-neutral-400 py-14">
                  <Receipt className="w-6 h-6 mx-auto mb-3 opacity-40" />
                  No invoices yet. Generate your first from a month of collections.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showGen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowGen(false)}>
          <div className="hbt-card p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="generate-invoice-modal">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="hbt-label">Auto-generate</div>
                <div className="font-display text-2xl font-semibold mt-1">Invoice from month’s collections</div>
              </div>
              <button onClick={() => setShowGen(false)} className="text-neutral-400 hover:text-black"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submitGen} className="space-y-4">
              <div>
                <label className="hbt-label block mb-2">Client</label>
                <select required className="hbt-input" value={gen.client_id} onChange={(e) => setGen({ ...gen, client_id: e.target.value })} data-testid="gen-client-select">
                  <option value="">Select…</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="hbt-label block mb-2">Period</label>
                  <select className="hbt-input" value={gen.month} onChange={(e) => setGen({ ...gen, month: e.target.value })} data-testid="gen-month-select">
                    {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="hbt-label block mb-2">VAT %</label>
                  <input type="number" step="0.1" className="hbt-input" value={gen.tax_rate} onChange={(e) => setGen({ ...gen, tax_rate: e.target.value })} data-testid="gen-tax-input" />
                </div>
                <div>
                  <label className="hbt-label block mb-2">Due (days)</label>
                  <input type="number" className="hbt-input" value={gen.due_days} onChange={(e) => setGen({ ...gen, due_days: e.target.value })} data-testid="gen-due-input" />
                </div>
              </div>
              <div className="text-xs text-neutral-500">
                Default rates (SZL/kg): general 4.50 · recyclable 2.00 · organic 3.50 · hazardous 12.00. Edit line items after generation.
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowGen(false)} className="btn-outline">Cancel</button>
                <button type="submit" disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="gen-submit">
                  <Wand2 className="w-4 h-4" /> {busy ? "Generating…" : "Generate"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
